import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  useGetAllCategoriesQuery,
  useUpdateCategoryMutation,
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
} from "../../services/modules/category";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedCategory,
  clearSelectedCategory,
  toggleForm,
} from "../../store/category";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const CategoryList = () => {
  const dispatch = useDispatch();
  const selectedCategory = useSelector(
    (state) => state.categories.selectedCategory
  );
  const isFormOpen = useSelector((state) => state.categories.isFormOpen);

  const { data, isLoading, refetch } = useGetAllCategoriesQuery();
  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const categories = data?.data?.result || [];
  const [orderedCategories, setOrderedCategories] = useState([]);

  const [form, setForm] = useState({ name: "", isHome: false, coverImage: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

 useEffect(() => {
  setOrderedCategories((prev) => {
    // Solo se la nuova lista è diversa, aggiorna!
    const newOrdered = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    if (JSON.stringify(prev.map(x=>x.id)) !== JSON.stringify(newOrdered.map(x=>x.id))) {
      return newOrdered;
    }
    return prev;
  });
}, [categories])

  useEffect(() => {
    if (selectedCategory) {
      setForm({
        name: selectedCategory.name || "",
        isHome: selectedCategory.isHome || false,
        coverImage: selectedCategory.coverImage || "",
      });
    } else {
      setForm({ name: "", isHome: false, coverImage: "" });
    }
  }, [selectedCategory]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
  };

  const openEditForm = (category) => {
    dispatch(setSelectedCategory(category));
    if (!isFormOpen) dispatch(toggleForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = form.name.trim();

    try {
      if (selectedCategory) {
        await updateCategory({
          id: selectedCategory.id,
          name: trimmedName,
          isHome: form.isHome,
          coverImage: form.coverImage,
        }).unwrap();
        toast.success("Categoria aggiornata!");
      } else {
        await createCategory({
          name: trimmedName,
          isHome: form.isHome,
          coverImage: form.coverImage,
        }).unwrap();
        toast.success("Categoria creata!");
      }

      dispatch(clearSelectedCategory());
      dispatch(toggleForm());
      setForm({ name: "", isHome: false, coverImage: "" });
      await refetch();
    } catch (err) {
      console.error("Errore salvataggio:", err);
      toast.error("Impossibile salvare la categoria.");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteId).unwrap();
      toast.success("Categoria cancellata!");
      setDeleteId(null);
      setShowDeleteModal(false);
      await refetch();
    } catch (err) {
      toast.error("Errore durante l'eliminazione.");
      console.error("Errore eliminazione:", err);
    }
  };

  const toggleHomepage = async (cat) => {
    try {
      await updateCategory({
        id: cat.id,
        name: cat.name,
        isHome: !cat.isHome,
        coverImage: cat.coverImage,
      }).unwrap();
      toast.success("Stato homepage aggiornato");
      await refetch();
    } catch (err) {
      console.error("Errore aggiornamento homepage:", err);
      toast.error("Errore aggiornamento stato homepage");
    }
  };

  const cancelForm = () => {
    setForm({ name: "", isHome: false, coverImage: "" });
    dispatch(clearSelectedCategory());
    dispatch(toggleForm());
  };

  // ---- DRAG & DROP ----
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedCategories.findIndex((cat) => cat.id.toString() === active.id);
    const newIndex = orderedCategories.findIndex((cat) => cat.id.toString() === over.id);

    const newOrdered = arrayMove(orderedCategories, oldIndex, newIndex);
    setOrderedCategories(newOrdered);
  };

  // Salva ordine backend
  const saveOrder = async () => {
    try {
      await Promise.all(
        orderedCategories.map((cat, idx) =>
          updateCategory({
            id: cat.id,
            name: cat.name,
            coverImage: cat.coverImage,
            isHome: cat.isHome,
            order: idx + 1,
          }).unwrap()
        )
      );
      toast.success("Ordine salvato!");
      await refetch();
    } catch (err) {
      toast.error("Errore nel salvataggio dell'ordine");
    }
  };

  return (
    <div className="p-4">
      <ToastContainer position="top-right" autoClose={3000} style={{ zIndex: 9999 }} />
      <h2 className="text-xl font-bold mb-6">Categoria</h2>

      {isLoading ? (
        <p>Loading categories...</p>
      ) : orderedCategories.length > 0 ? (
        <>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="min-w-full bg-white border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {/* <th className="border px-2 py-1">Ordina</th> */}
                  <th className="border px-2 py-1">ID</th>
                  <th className="border px-2 py-1">Descrizione</th>
                  <th className="border px-2 py-1">Copertina</th>
                  <th className="border px-2 py-1">Azione</th>
                </tr>
              </thead>
              <SortableContext
                items={orderedCategories.map((cat) => cat.id.toString())}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {orderedCategories.map((cat) => (
                    <SortableRow
                      key={cat.id}
                      cat={cat}
                      openEditForm={openEditForm}
                      setDeleteId={setDeleteId}
                      setShowDeleteModal={setShowDeleteModal}
                      toggleHomepage={toggleHomepage}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
          <div className="flex justify-end mt-3">
            {/* <button
              onClick={saveOrder}
              className="bg-red-600 text-white px-4 py-2 rounded"
            >
              Salva Ordine
            </button> */}
          </div>
        </>
      ) : (
        <p className="text-gray-500">No categorie trovate.</p>
      )}

      {isFormOpen ? (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <input
            type="text"
            name="name"
            placeholder="Nome Categoria"
            value={form.name}
            onChange={handleInputChange}
            className="border p-2 w-full rounded"
            required
          />
          <input
            type="text"
            name="coverImage"
            placeholder="URL immagine di copertina"
            value={form.coverImage}
            onChange={handleInputChange}
            className="border p-2 w-full rounded"
          />

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={cancelForm}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              {selectedCategory ? "Update" : "Crea"}
            </button>
          </div>
        </form>
      ) : (
        <div className="flex justify-end mt-6">
          <button
            onClick={() => dispatch(toggleForm())}
            className="bg-gray-800 text-white px-5 py-2 rounded"
          >
            Aggiungi Nuova Categoria
          </button>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded p-6 shadow-md text-center space-y-4 w-96">
            <h3 className="text-lg font-semibold">Conferma Eliminazione</h3>
            <p>Sei sicuro che vuoi cancellare questa categoria?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId(null);
                }}
                className="px-4 py-2 rounded bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded bg-red-500 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;

// --- SORTABLE ROW ---

const SortableRow = ({
  cat,
  openEditForm,
  setDeleteId,
  setShowDeleteModal,
  toggleHomepage,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: cat.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <tr ref={setNodeRef} style={style} className="cursor-grab hover:bg-gray-50">
      {/* <td className="border px-2 py-1 w-[100px]" {...attributes} {...listeners} >
        <svg width="24" height="24" className="inline" fill="gray"><circle cx="12" cy="12" r="10" /></svg>
      </td> */}
      <td className="border px-2 py-1">{cat.id}</td>
      <td className="border px-2 py-1">{cat.name || "-"}</td>
      <td className="border px-2 py-1 w-[300px]">
        {cat.coverImage ? (
          <img
            src={cat.coverImage}
            alt="cover"
            className="w-12 h-12 object-cover rounded"
          />
        ) : "-"}
      </td>
      <td className="border px-2 py-1 text-left w-[300px] overflow-hidden">
        <div className="space-x-2 flex flex-wrap items-center">
          <button
            onClick={() => {
              setDeleteId(cat.id);
              setShowDeleteModal(true);
            }}
            className="px-3 py-1 bg-gray-100 border border-gray-300 rounded"
          >
            Elimina
          </button>
          <button
            onClick={() => openEditForm(cat)}
            className="px-3 py-1 bg-gray-100 border border-gray-300 rounded"
          >
            Modifica
          </button>
          {/* <input
            type="checkbox"
            checked={cat.isHome === true}
            onChange={() => toggleHomepage(cat)}
            className="h-4 w-4 mr-2"
            title="Mostra in homepage"
          />
          <label>Homepage</label> */}
        </div>
      </td>
    </tr>
  );
};
